<script setup lang="ts">
import type { AwardConfig } from '~/types/appTypes'

const app = useNuxtApp()
const route = useRoute()
const kijidasConfig = useAppConfig().kijidas
const storeIndex = useStoreIndex()
const storeMuseumChar = useStoreMuseumChar()

definePageMeta({ layout: 'layout-side-default' })

const awardConfigList = useAppConfig().app.award
const awardConfig = awardConfigList[route.params.award as string] as AwardConfig

const year = app.$anyToInteger(route.params.year)
const slug = route.params.slug as string

const pageData = { item: null as MuseumCharItem | null }

const refData = reactive({
  entryMode: null,
  toComment: {},
  isPreview: false,
})

const setHead = () => {
  const baseTitle = awardConfig.baseTitle
  const breadcrumbTitle = awardConfig.breadcrumbTitle || baseTitle
  const headTitle = `${baseTitle}${year}`
  const headDescription = awardConfig.description

  // ぱんくず作成
  storeIndex.setBreadCrumbs([
    {
      text: breadcrumbTitle,
      disabled: false,
      to: {
        name: 'award',
        params: { award: awardConfig.awardSlug },
      },
    },
    {
      text: headTitle,
      disabled: false,
      to: {
        name: 'award-year',
        params: {
          award: awardConfig.awardSlug,
          year: year,
        },
      },
    },
    {
      text: pageData.item.title,
      disabled: true,
    },
  ])

  const headInput = app.$makeMeta({
    title: headTitle,
    description: headDescription,
  })
  useHead(headInput)
}

const initPage = async () => {
  const [_resSite, _resMaster, charaItems] = await Promise.all([
    storeIndex.fetchSite(),
    storeIndex.fetchMaster(),
    storeMuseumChar.fetchCharaList(kijidasConfig, awardConfig.awardSlug, year, 0, slug),
  ])

  if (route.name === 'preview-museum-chara-year-slug') {
    //  プレビューモード
    refData.entryMode = 'in'
    refData.isPreview = true
  } else {
    refData.entryMode = app.$isAwardEntry(awardConfig, year)
    if (refData.entryMode === null) {
      //  表示できない
      throw createError({
        statusCode: 999,
        statusMessage: 'このアワードは現在表示できません',
      })
      return
    }
  }
  // // todo: 本来は前後のキャラリンクに移動するため、キャラの一覧を取得するのだが、本物もエラーで表示されていない
  if (!charaItems?.length) {
    throw createError({ statusCode: 404 })
    return
  }
  pageData.item = charaItems[0]

  refData.toComment = {
    name: 'award-year-slug-comment',
    params: {
      award: awardConfig.awardSlug,
      year: year,
      slug: slug,
    },
  }
}
await initPage()
setHead()
</script>

<template>
  <div class="p-articlePage">
    <div class="p-museumChar">
      <h1 class="c-heading_page">
        {{ pageData.item.title }}
      </h1>
      <nuxt-link
        v-if="refData.entryMode !== 'before'"
        class="p-museumChar_toComment"
        v-bind:to="refData.toComment"
      >
        この{{ awardConfig.labelItemShort }}の応援メッセージを見る
      </nuxt-link>
      <template
        v-if="pageData.item"
      >
        <div
          id="staticBody"
          v-bind:class="{ 'is-old': pageData.item.isOld }"
          v-html="app.$getBody(pageData.item.bodyJson)"
        />
      </template>
      <nuxt-link
        v-if="refData.entryMode !== 'before'"
        class="p-museumChar_toComment"
        v-bind:to="refData.toComment"
      >
        この{{ awardConfig.labelItemShort }}の応援メッセージを見る
      </nuxt-link>

      <div class="p-museumChar_head">
        <img
          class="p-museumChar_head_icon"
          src="@/assets/images/icon-title.png"
        >
        <span
          class="p-museumChar_head_title"
        >
          この{{ awardConfig.labelItemShort }}に投票する
        </span>
      </div>

      <template
        v-if="refData.isPreview"
      >
        <div class="p-museumChar_pastEntry">
          プレビューなので投票できません。
        </div>
      </template>
      <template
        v-else-if="refData.entryMode === 'before'"
      >
        <div class="p-museumChar_pastEntry">
          投票受付開始前です。
        </div>
      </template>
      <template
        v-else-if="refData.entryMode === 'in'"
      >
        <AwardForm
          v-bind:slug="slug"
          v-bind:article="awardConfig.awardSlug"
        />
      </template>
      <template
        v-else
      >
        <div class="p-museumChar_pastEntry">
          投票は締め切りました。たくさんの応援ありがとうございました。
        </div>
      </template>
    </div>
  </div>
</template>

<style lang = "scss" scoped>
@use "@/assets/css/foundation/mixin/responsive" as responsive;

#staticBody {
  padding: 30px 0;

  &.is-old {
    :deep(> *:not(table)) {
      display: none;
    }

    :deep(> table) {
      width: 100%;

      > tbody {
        tr:first-child,
        tr:nth-child(2),
        tr:last-child {
          display: none;
        }
      }
    }
  }
}

.p-articlePage {
  &_item {
    display: block;
  }
}

.p-museumChar {
  &_head {
    display: flex;
    align-items: flex-start;
    margin-top: 30px;
    margin-bottom: 10px;
    padding-bottom: 12px;
    border-bottom: solid 1px var(--g_color_border_gray);

    &_icon {
      margin-top: 3px;
      margin-right: 15px;

      @include responsive.mq_down(sp) {
        width: 20px;
        height: 20px;
      }
    }

    &_title {
      display: inline-block;
      position: relative;
      width: 100%;
      margin-right: 10px;
      font-family: var(--g_font_family_SawarabiGothic);
      font-size: 20px;
      font-weight: bold;
      line-height: 1.5;

      @include responsive.mq_down(sp) {
        font-size: 18px;
      }
    }
  }

  &_pastEntry {
    margin: 20px 0;
    padding: 20px;
    background-color: #ddd;
    color: var(--color_error);
    font-size: 20px;
    font-weight: bold;
    text-align: center;

    @include responsive.mq_down(sp) {
      padding: 15px;
      font-size: 16px;
    }
  }

  &_advertise {
    display: flex;
    justify-content: center;
    margin: 50px 0;

    &_unit {
      width: 334px;
      height: 280px;

      @include responsive.mq_down(sp) {
        width: 100%;
        max-width: 334px;
      }
    }
  }
}
</style>
